// From: https://github.com/prisma-labs/graphql-request/issues/269

const PERSISTED_QUERY_NOT_FOUND_CODE = 'HC0020' // HotChocolate APQ code
const VERSION = 1

type Fetch = typeof fetch

/**
 * Creates a fetch implementation that sends GraphQL persisted query requests.
 */
export const createPersistedQueryFetch =
  (fetchImpl: Fetch): Fetch =>
  async (input, init) => {
    const request = { input, init } as Request
    let processor

    if (request.init?.method === 'POST') {
      // We want to change the request to a GET request unless it's a mutation
      const isMutation =
        typeof request.init?.body === 'string' && request.init.body.includes('mutation')

      if (isMutation) {
        return fetchImpl(input, init)
      }

      processor = getPostToGetRequestProcessor()
    } else {
      processor = getRequestProcessor(request)
    }

    const requestWithQueryHash = await processor.addHash(request)
    const requestWithoutQuery = processor.removeQuery(requestWithQueryHash)
    requestWithoutQuery.init = requestWithoutQuery.init || {}
    requestWithoutQuery.init.method = 'GET'

    const res = await fetchImpl(requestWithoutQuery.input, requestWithoutQuery.init)
    if (isPersistedQueryResponseCodeNotOk(res)) {
      return retryWithQuery(request, fetchImpl)
    }

    // clone the response so that we can read the body twice
    const clonedRes = res.clone()

    const body = await clonedRes.json()

    // if the query was not found in the server send another request with the query
    if (isPersistedQueryNotFoundError(body)) {
      return retryWithQuery(request, fetchImpl)
    } else {
      return res
    }
  }

/**
 * Manipulates a fetch request, implemented per HTTP method type.
 */
interface RequestProcessor {
  /**
   * Removes the GraphQL query argument from the request
   */
  removeQuery(request: Request): Request

  /**
   * Adds the GraphQL request query hash to the request
   */
  addHash(request: Request): Promise<Request>
}

async function retryWithQuery(request: Request, fetchImpl: Fetch) {
  const originalMethod = request.init?.method || 'GET'
  const processor = getRequestProcessor(request)
  const requestWithQuery = await processor.addHash(request)

  // Modify the method back to the original if it was not GET
  if (originalMethod !== 'GET' && requestWithQuery.init) {
    requestWithQuery.init.method = originalMethod
  }

  return await fetchImpl(requestWithQuery.input, requestWithQuery.init)
}

function getPostToGetRequestProcessor() {
  const method = 'PERSISTEDQUERY'
  const requestProcessor = requestProcessorByMethod[method]

  if (!requestProcessor) {
    throw new Error('Unsupported request method: ' + method)
  }

  return requestProcessor
}

function getRequestProcessor(request: Request) {
  const method = (request.init?.method ?? 'GET').toUpperCase()
  const requestProcessor = requestProcessorByMethod[method]

  if (!requestProcessor) {
    throw new Error('Unsupported request method: ' + method)
  }

  return requestProcessor
}

const requestProcessorByMethod: Record<string, RequestProcessor> = {
  GET: {
    removeQuery: request => {
      const [url, params] = splitUrlAndSearchParams(getRequestInputUrl(request.input))
      params.delete('query')
      return {
        ...request,
        input: requestInfoWithUpdatedUrl(request.input, `${url}?${params.toString()}`),
      }
    },
    addHash: async request => {
      const [url, params] = splitUrlAndSearchParams(getRequestInputUrl(request.input))

      const query = params.get('query')

      if (!query) {
        throw new Error('GET request must contain a query parameter')
      }

      const hash = await sha256(query)

      params.append(
        'extensions',
        JSON.stringify({
          persistedQuery: {
            version: VERSION,
            sha256Hash: hash,
          },
        })
      )

      return {
        ...request,
        input: requestInfoWithUpdatedUrl(request.input, `${url}?${params.toString()}`),
      }
    },
  },
  POST: {
    removeQuery: request => {
      if (typeof request.init?.body !== 'string') {
        throw new Error('POST request must contain a body')
      }

      const body = JSON.parse(request.init.body)
      const { query, ...bodyWithoutQuery } = body

      return {
        ...request,
        init: {
          ...request.init,
          body: JSON.stringify(bodyWithoutQuery),
        },
      }
    },
    addHash: async request => {
      if (typeof request.init?.body !== 'string') {
        throw new Error('POST request must contain a body')
      }

      const body = JSON.parse(request.init.body)

      if (typeof body.query !== 'string') {
        throw new Error('POST request body must contain a query')
      }

      const hash = await sha256(body.query)

      return {
        ...request,
        init: {
          ...request.init,
          body: JSON.stringify({
            ...body,
            extensions: {
              persistedQuery: {
                version: VERSION,
                sha256Hash: hash,
              },
            },
          }),
        },
      }
    },
  },
  PERSISTEDQUERY: {
    removeQuery: request => {
      return {
        ...request,
        init: {
          ...request.init,
          body: null,
        },
      }
    },
    addHash: async request => {
      const [url, params] = splitUrlAndSearchParams(getRequestInputUrl(request.input))

      if (typeof request.init?.body !== 'string') {
        throw new Error('PERSISTEDQUERY request must contain a body')
      }

      const body = JSON.parse(request.init.body)

      if (typeof body.query !== 'string') {
        throw new Error('PERSISTEDQUERY request body must contain a query')
      }

      const variables = body.variables ? JSON.stringify(body.variables) : null
      const hash = await sha256(body.query)

      params.append('operationName', body.operationName)
      params.append(
        'extensions',
        JSON.stringify({
          persistedQuery: {
            version: VERSION,
            sha256Hash: hash,
          },
        })
      )

      if (variables) {
        params.append('variables', variables)
      }

      return {
        ...request,
        input: requestInfoWithUpdatedUrl(request.input, `${url}?${params.toString()}`),
      }
    },
  },
}

interface Request {
  input: RequestInfo
  init?: RequestInit
}

function requestInfoWithUpdatedUrl(info: RequestInfo, url: string): RequestInfo {
  return typeof info === 'string'
    ? url
    : {
        ...info,
        url,
      }
}

function getRequestInputUrl(input: RequestInfo) {
  return typeof input === 'string' ? input : input.url
}

function splitUrlAndSearchParams(
  url: string
): [urlWithoutSearchParams: string, params: URLSearchParams] {
  const startOfSearchParams = url.indexOf('?')

  return startOfSearchParams === -1
    ? [url, new URLSearchParams()]
    : [url.slice(0, startOfSearchParams), new URLSearchParams(url.slice(startOfSearchParams))]
}

/**
 * Copied from https://newbedev.com/javascript-sha256-hash-js-code-example
 */
async function sha256(message: string) {
  // encode as UTF-8
  const msgBuffer = new TextEncoder().encode(message)

  // hash the message
  // eslint-disable-next-line no-restricted-globals
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer)

  // convert ArrayBuffer to Array
  const hashArray = Array.from(new Uint8Array(hashBuffer))

  // convert bytes to hex string
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}

interface GraphQLResponse {
  status?: number
  errors?: {
    extensions?: {
      code?: string
    }
  }[]
}

function isPersistedQueryNotFoundError(resBody: GraphQLResponse) {
  return (
    resBody.errors &&
    resBody.errors.length > 0 &&
    resBody.errors.find(err => err.extensions?.code === PERSISTED_QUERY_NOT_FOUND_CODE) != null
  )
}

function isPersistedQueryResponseCodeNotOk(resBody: GraphQLResponse) {
  return resBody.status && resBody.status !== 200
}
